/**
 * Prospect sub-stage follow-up rules — the one client copy (owner 2026-10-23).
 *
 * Shared by the stage-status dialog (`StageStatusFollowUpDialog`), the hook that
 * opens it (`use-stage-status-change`), the Prospect Follow-ups tracker and the
 * University Partnerships "Follow-ups due" tile. Keep one copy of each rule:
 * never re-inline these labels or dates in a page.
 *
 * The rules, all decided by the status row's FLAGS, never by its (admin-
 * renamable) name:
 *
 * - `resurfaceMode: 'date'` — choosing the status REQUIRES a follow-up date.
 *   The dialog offers +3/+6/+9/+12 month quick picks plus a date input.
 * - `resurfaceMode: 'next_cycle'` (LOB) — the follow-up date defaults to the
 *   next 1 March strictly after today (cycles start 1 April, so this is a month
 *   of runway before the new cycle). The person may override it.
 * - `requiresReason` — choosing the status REQUIRES a reason; the server writes
 *   it as a system note on the company.
 * - `followUpSlaDays` — no dialog; the SLA clock is the server's business.
 *
 * Dates are bare 'YYYY-MM-DD' strings throughout. The month arithmetic below is
 * pure string/number work with no `Date` at all, so no timezone can move a day.
 * The server validates a follow-up date against today in UTC, so callers that
 * need a "today" to feed these helpers (or a `min` for a date input) should use
 * `todayUtcIsoDate`, not the viewer-local `todayIsoDate` from invoice-utils.
 */
import { addDaysIso } from '@/lib/quote-utils';
import type { CompanyStageStatus, LastContactKind, ProspectFollowUpBucket } from '@/lib/types';

/**
 * Today's calendar date in UTC, as 'YYYY-MM-DD'.
 *
 * Assembled from the UTC getters rather than `toISOString()` so the intent is
 * explicit. UTC (not the viewer's local day) because the server's
 * `FOLLOW_UP_DATE_INVALID` check and its next-cycle default both run on UTC
 * today; a viewer east of Greenwich after midnight would otherwise be offered
 * a "tomorrow" the server still calls today.
 */
export function todayUtcIsoDate(now: Date = new Date()): string {
  const month = String(now.getUTCMonth() + 1).padStart(2, '0');
  const day = String(now.getUTCDate()).padStart(2, '0');
  return `${now.getUTCFullYear()}-${month}-${day}`;
}

/** The day after `iso` — the earliest follow-up date the server accepts. */
export function nextDayIso(iso: string): string {
  return addDaysIso(iso, 1);
}

/**
 * The next 1 March strictly after `todayIso`.
 *
 * Mirrors the server's `nextCycleFollowUpDate` exactly: on 1 March itself the
 * answer is NEXT year's 1 March, never today. ISO dates sort lexicographically,
 * so a string comparison is the whole test.
 */
export function nextCycleFollowUpDate(todayIso: string): string {
  const year = Number(todayIso.slice(0, 4));
  const thisYear = `${String(year).padStart(4, '0')}-03-01`;
  if (thisYear > todayIso) return thisYear;
  return `${String(year + 1).padStart(4, '0')}-03-01`;
}

function daysInMonth(year: number, month: number): number {
  // month is 1..12. February follows the Gregorian leap rule.
  if (month === 2) {
    const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    return leap ? 29 : 28;
  }
  return month === 4 || month === 6 || month === 9 || month === 11 ? 30 : 31;
}

/**
 * Add whole calendar months to a 'YYYY-MM-DD' string, clamping to month end.
 *
 * 31 Jan + 1 month is 28 Feb (29 in a leap year), not 3 March — which is what
 * `new Date(y, m + n, d)` overflow would give. Negative `months` works too.
 */
export function addMonthsIso(iso: string, months: number): string {
  const year = Number(iso.slice(0, 4));
  const month = Number(iso.slice(5, 7));
  const day = Number(iso.slice(8, 10));
  const zeroBased = month - 1 + months;
  const targetYear = year + Math.floor(zeroBased / 12);
  const targetMonth = (((zeroBased % 12) + 12) % 12) + 1;
  const targetDay = Math.min(day, daysInMonth(targetYear, targetMonth));
  return `${String(targetYear).padStart(4, '0')}-${String(targetMonth).padStart(2, '0')}-${String(targetDay).padStart(2, '0')}`;
}

/** Quick picks for a `resurfaceMode: 'date'` status, measured from today. */
export const FOLLOW_UP_QUICK_PICKS: readonly { label: string; months: number }[] = [
  { label: '3 months', months: 3 },
  { label: '6 months', months: 6 },
  { label: '9 months', months: 9 },
  { label: '12 months', months: 12 },
];

/**
 * Whether choosing `status` must go through the follow-up dialog first.
 *
 * True for a resurface status (it needs a date) or one that requires a reason.
 * Clearing the status (`null`) and an older API that omits the flags
 * (`undefined`) both need no dialog: the save goes straight through, and the
 * server's 422 is still the real boundary.
 */
export function needsFollowUpDialog(status: CompanyStageStatus | null | undefined): boolean {
  if (!status) return false;
  if (status.resurfaceMode === 'date' || status.resurfaceMode === 'next_cycle') return true;
  return status.requiresReason === true;
}

export const BUCKET_LABELS: Record<ProspectFollowUpBucket, string> = {
  overdue: 'Overdue',
  due_soon: 'Due soon',
  on_track: 'On track',
  resurfacing: 'Resurfacing',
  waiting: 'Waiting',
  no_sub_stage: 'No sub-stage',
};

export const LAST_CONTACT_KIND_LABELS: Record<LastContactKind, string> = {
  email: 'Email',
  note: 'Note',
  task: 'Task',
  quote: 'Quote sent',
  signature: 'Signature request',
};

/**
 * Badge tone per bucket (all names are `BadgeTone` members in components/ui/Badge).
 * Tone is never the only signal — always render `BUCKET_LABELS` beside it.
 */
export function bucketTone(bucket: ProspectFollowUpBucket): 'danger' | 'warning' | 'neutral' | 'success' | 'info' {
  switch (bucket) {
    case 'overdue':
      return 'danger';
    case 'due_soon':
      return 'warning';
    case 'on_track':
      return 'success';
    case 'resurfacing':
      return 'info';
    case 'waiting':
    case 'no_sub_stage':
      return 'neutral';
  }
}
